import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/site.config";

export default function FloatingCTA() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const bookHref = isLanding ? "#contact" : "/#contact";

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid="floating-cta"
          className="fixed z-[68] bottom-4 right-4 md:bottom-8 md:right-8 flex flex-col items-end gap-3"
        >
          {/* Book button — desktop only OR mobile expanded */}
          <a
            href={bookHref}
            data-testid="floating-book"
            className={`${
              expanded ? "flex" : "hidden md:inline-flex"
            } group items-center gap-3 rounded-full bg-[#D4AF37] text-black px-5 py-3.5 text-sm font-medium hover:bg-[#f3d568] transition-colors shadow-[0_10px_40px_rgba(212,175,55,0.25)]`}
            onClick={() => setExpanded(false)}
          >
            Book a Paid Call
            <span className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center group-hover:translate-x-1 transition-transform">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </span>
          </a>

          {/* WhatsApp — desktop pill OR mobile expanded pill */}
          <a
            href={BRAND.whatsappUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="floating-whatsapp"
            className={`${
              expanded ? "flex" : "hidden md:inline-flex"
            } group items-center gap-3 pl-4 pr-5 py-3 rounded-full backdrop-blur-2xl bg-black/60 border border-white/10 text-white hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-colors shadow-[0_10px_40px_rgba(0,0,0,0.5)]`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.001 2C6.478 2 2 6.478 2 12c0 1.86.508 3.598 1.386 5.087L2 22l4.995-1.36A9.958 9.958 0 0012.001 22c5.523 0 10-4.478 10-10s-4.477-10-10-10z" />
            </svg>
            <span className="text-sm">WhatsApp Ritesh</span>
          </a>

          {/* Mobile-only compact toggle (icon) */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Close quick actions" : "Open quick actions"}
            data-testid="floating-toggle"
            className={`md:hidden w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_40px_rgba(212,175,55,0.35)] transition-colors ${
              expanded
                ? "bg-black/70 backdrop-blur-2xl border border-white/15 text-white"
                : "bg-[#D4AF37] text-black hover:bg-[#f3d568]"
            }`}
          >
            {expanded ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 6l12 12M6 18L18 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.001 2C6.478 2 2 6.478 2 12c0 1.86.508 3.598 1.386 5.087L2 22l4.995-1.36A9.958 9.958 0 0012.001 22c5.523 0 10-4.478 10-10s-4.477-10-10-10z" />
              </svg>
            )}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
